/*
 * Used for CKEditor embed videos using oembed. We need to convert the oembed into an iframe
 * Reconfigured and based on: https://gist.github.com/johnfmorton/6b7961fb13e09fe3ef8d5d19ed657614
 * Use for YouTube and Vimeo videos only
 *
 * Should other platforms be required - would need to look at using a service like iframely (https://iframely.com) and use of an APIKey instead.
 */

// Function to extract the video ID from various URL formats
import {findMany} from '../helpers.js';

// Find all oembed elements and replace them with iframes
const oembedElements = findMany('oembed[url]');

const extractVideoId = (url, regex) => {
    const matches = url.match(regex);

    return matches ? matches[1] : null;
};

// Function to create the iframe element
const createIframe = (src) => {
    const iframe = document.createElement('iframe');

    // Setting attributes
    Object.assign(iframe, {
        width: '1280',
        height: '720',
        frameBorder: '0',
        allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
        allowFullscreen: true,
        title: 'Video player',
        loading: 'lazy',
    });

    // Cookiebot attributes (must use setAttribute)
    iframe.setAttribute('data-cookieblock-src', src);
    iframe.setAttribute('data-cookieconsent', 'marketing');

    return iframe;
};

// Function to create a fallback message for blocked videos
const createConsentMessage = () => {
    const message = document.createElement('div');

    message.className = 'cookieconsent-optout-marketing';
    message.innerHTML = `
        Please <a href="javascript:Cookiebot.renew()">accept marketing cookies</a> to watch this video.
    `;

    return message;
};

// Define video platforms with their respective regex patterns and embed URLs
const videoPlatforms = [
    {
        name: 'YouTube',
        embedUrl: (id) => `https://www.youtube.com/embed/${id}`,
        regex: /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    },
    {
        name: 'Vimeo',
        embedUrl: (id) => `https://player.vimeo.com/video/${id}`,
        regex: /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:.*#|.*\/videos\/)?([0-9]+)/,
    },
];

const containerMap = new Map();

oembedElements.forEach((element) => {
    const url = element.getAttribute('url');

    if (!url) {
        return;
    }

    const platform = videoPlatforms.find((p) => p.regex.test(url));

    if (!platform) {
        return;
    }

    const videoId = extractVideoId(url, platform.regex);

    if (!videoId) {
        return;
    }

    const container = document.createElement('div');

    container.className = 'aspect-video flex items-center justify-center bg-neutral-100';

    // Check if marketing cookies are accepted
    if (Cookiebot && Cookiebot.consent.marketing) {
        container.appendChild(createIframe(platform.embedUrl(videoId)));
    } else {
        container.appendChild(createConsentMessage());
    }

    element.parentNode.replaceChild(container, element);

    // Save reference
    containerMap.set(element, {container, platform, videoId});
});

const handleConsentChange = (isAccepted) => {
    containerMap.forEach(({container, platform, videoId}) => {
        container.innerHTML = '';

        if (isAccepted && Cookiebot.consent.marketing) {
            // User accepted marketing cookies → inject iframe
            container.appendChild(createIframe(platform.embedUrl(videoId)));
        } else {
            // User declined marketing cookies → show fallback
            container.appendChild(createConsentMessage());
        }
    });
};

window.addEventListener('CookiebotOnAccept', () => {
    handleConsentChange(true);
});

window.addEventListener('CookiebotOnDecline', () => {
    handleConsentChange(false);
});
